import React, { forwardRef } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import EditionsIcon from '@atlaskit/icon-lab/core/editions';
import { Box, Inline, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { type DiscoveryCTAButtonProps } from './types';

const styles = cssMap({
	base: {
		display: 'inline-flex',
		paddingRight: token('space.150'),
		paddingLeft: token('space.100'),
		paddingTop: '0',
		paddingBottom: '0',
		textDecoration: 'none',
		borderRadius: token('radius.medium'),
		justifyContent: 'center',
		alignItems: 'center',
		width: 'fit-content',
		transition: 'background 0.1s ease-out',
		whiteSpace: 'nowrap',
	},
	defaultSpacing: {
		minHeight: '32px',
	},
	compactSpacing: {
		minHeight: '24px',
	},
	default: {
		color: token('color.text.inverse'),
		backgroundColor: token('color.background.accent.purple.bolder'),
		'&:hover': {
			backgroundColor: token('color.background.accent.purple.bolder.hovered'),
			textDecoration: 'none',
			color: token('color.text.inverse'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.purple.bolder.pressed'),
			textDecoration: 'none',
		},
	},
	inverted: {
		color: token('color.text.discovery'),
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border.accent.purple'),
		'&:hover': {
			backgroundColor: token('color.background.accent.purple.subtlest.hovered'),
			textDecoration: 'none',
			color: token('color.text.discovery'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.purple.subtlest.pressed'),
			textDecoration: 'none',
		},
	},
	subtle: {
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.accent.purple'),
		whiteSpace: 'nowrap',
		'&:hover': {
			backgroundColor: token('color.background.accent.purple.subtlest.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.accent.purple.subtlest.pressed'),
		},
	},
	contentContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
	},
	iconContainer: {
		display: 'flex',
		width: '24px',
		height: '24px',
		justifyContent: 'center',
		alignItems: 'center',
	},
});

export const DiscoveryCTAButton: React.ForwardRefExoticComponent<
	Omit<DiscoveryCTAButtonProps, 'ref'> & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, DiscoveryCTAButtonProps>(
	({ buttonStyle, children, spacing, ...buttonProps }, ref) => {
		return (
			<Pressable
				ref={ref}
				{...buttonProps}
				xcss={cx(
					styles.base,
					buttonStyle === 'default' && styles.default,
					buttonStyle === 'inverted' && styles.inverted,
					buttonStyle === 'subtle' && styles.subtle,
					spacing === 'compact' ? styles.compactSpacing : styles.defaultSpacing,
				)}
			>
				<Box xcss={styles.contentContainer}>
					<Box xcss={styles.iconContainer}>
						<EditionsIcon label="" />
					</Box>
					<Inline>{children}</Inline>
				</Box>
			</Pressable>
		);
	},
);
